import { Component, ChangeDetectionStrategy, signal, inject, OnInit } from '@angular/core';
import { Router, RouterOutlet, NavigationEnd } from '@angular/router';
import { SidebarComponent } from './components/sidebar/sidebar.component';
import { NavbarComponent } from './components/navbar/navbar.component';
import { GlobalSearchLauncherComponent } from './components/global-search/global-search-launcher.component';
import { CallbackComponent } from './components/callback/callback.component';
import { MatIconModule } from '@angular/material/icon';
import { AuthService } from './services/auth.service';
import { isPublicAppPath } from './guards/public-path';
import { filter } from 'rxjs/operators';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService } from 'primeng/api';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [RouterOutlet, SidebarComponent, NavbarComponent, GlobalSearchLauncherComponent, CallbackComponent, MatIconModule, ConfirmDialogModule],
  providers: [ConfirmationService]
})
export class AppComponent implements OnInit {
  private authService = inject(AuthService);
  private router = inject(Router);

  isSidebarOpen = signal(false);
  currentRoute = signal('');
  /** Reactivo al estado real de AuthService (localStorage / expiración). */
  readonly isAuthenticated = this.authService.isAuthenticated;
  isCallback = signal(false);
  /** Rutas públicas a pantalla completa (sin auth, sin sidebar/navbar): /callback/..., /public/... */
  isPublicFullScreenRoute = signal(false);

  private static isPublicFullScreenPath(path: string): boolean {
    return isPublicAppPath(path);
  }

  /** Solo la ruta exacta del redirect URI de Keycloak; no usar `includes('code=')` en la query (p. ej. `referrer_code=` lo dispara por error). */
  private static isKeycloakCallbackPath(path: string): boolean {
    return (path || '') === '/callback';
  }

  constructor() {
    // Verificación sincrónica para el primer render (evita mostrar el portal brevemente)
    if (typeof window !== 'undefined') {
      const path = window.location.pathname;
      const search = window.location.search;
      this.currentRoute.set(path + search);
      this.isCallback.set(AppComponent.isKeycloakCallbackPath(path));
      this.isPublicFullScreenRoute.set(
        AppComponent.isPublicFullScreenPath(path) || this.isCallback()
      );
    }

    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: unknown) => {
      const nav = event as NavigationEnd;
      const fullUrl = nav.url ?? '';
      const path = fullUrl.split('?')[0];
      this.currentRoute.set(fullUrl);
      this.isCallback.set(AppComponent.isKeycloakCallbackPath(path));
      this.isPublicFullScreenRoute.set(
        AppComponent.isPublicFullScreenPath(path) || this.isCallback()
      );

      if (!this.isPublicFullScreenRoute()) {
        this.authService.ensureAuthenticatedSession();
      }
    });
  }

  ngOnInit(): void {
    // Usar la URL real del navegador para la decisión de redirección (el Router puede no estar listo aún al cargar directamente)
    if (typeof window !== 'undefined') {
      const path = window.location.pathname;
      const search = window.location.search;
      this.currentRoute.set(path + search);
      this.isCallback.set(AppComponent.isKeycloakCallbackPath(path));
      this.isPublicFullScreenRoute.set(
        AppComponent.isPublicFullScreenPath(path) || this.isCallback()
      );
    } else {
      this.currentRoute.set(this.router.url);
      const path = this.router.url.split('?')[0];
      this.isPublicFullScreenRoute.set(
        AppComponent.isPublicFullScreenPath(path) || this.isCallback()
      );
    }

    if (!this.isPublicFullScreenRoute()) {
      this.authService.ensureAuthenticatedSession();
    }
  }

  toggleSidebar(): void {
    this.isSidebarOpen.update(value => !value);
  }

  closeSidebar(): void {
    this.isSidebarOpen.set(false);
  }
}
